import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Routes, Route, useNavigate, useParams, Link } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableDropdownBox } from "@/components/SearchableDropdownBox";
import { ArrowLeft, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import {
  CANONICAL_ROWS,
  buildHourlySlots,
  buildObservationSlots,
  buildTwoHalfWorkingIntervals,
  computeWorkingHoursFromShift,
  defaultFormData,
  mergeFormData,
  normalizeKey,
} from "@/config/inprocessInspectionForm";
import { classifyCell } from "@/utils/inprocessSpecParse";
import { formatSpecificationDisplay } from "@/utils/specificationDisplay";

function aggregateRunTotals(run) {
  if (!run) return { target: 0, produced: 0, rejected: 0 };
  const parts = run.parts && run.parts.length ? run.parts : [];
  if (parts.length) {
    return {
      target: parts.reduce((s, p) => s + (Number(p.target_qty) || 0), 0),
      produced: parts.reduce((s, p) => s + (Number(p.produced_qty) || 0), 0),
      rejected: parts.reduce((s, p) => s + (Number(p.rejected_qty) || 0), 0),
    };
  }
  const m = run.metrics || {};
  return {
    target: Number(m.target_qty) || 0,
    produced: Number(m.produced_qty) || 0,
    rejected: Number(m.rejected_qty) || 0,
  };
}

/** Avoid false mismatch hints when summed floats differ only by epsilon (e.g. 947 vs 947.00000000002). */
function isNearlyEqual(a, b, eps = 1e-6) {
  if (a === b) return true;
  const na = Number(a);
  const nb = Number(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) return false;
  return Math.abs(na - nb) <= eps;
}

function computePerHourTargetFromCycle(cycleSec, shiftStart, shiftEnd, hourSlotCount) {
  const c = parseInt(cycleSec, 10);
  if (Number.isNaN(c) || c < 0) return null;
  if (!shiftStart || !shiftEnd || !hourSlotCount || hourSlotCount <= 0) return null;
  const workingHours = computeWorkingHoursFromShift(shiftStart, shiftEnd);
  const realisticCycleTime = c + 8;
  if (realisticCycleTime <= 0) return null;
  // Keep total target aligned with production-run logic.
  const shiftTarget = Math.round((workingHours * 3600) / realisticCycleTime);
  return shiftTarget / hourSlotCount;
}

function formatTargetNumber(n) {
  if (n == null || Number.isNaN(n)) return "";
  // Keep raw logic, but strip floating-point noise (e.g. 947.00000000002 -> 947).
  const nearestInt = Math.round(n);
  if (Math.abs(n - nearestInt) < 1e-9) return String(nearestInt);
  return String(Number(n.toPrecision(12)));
}

function inferRowMeta(specification, canonical) {
  const s = String(specification || "").trim();
  const up = s.toUpperCase();
  // Explicit tolerance spec must always use numeric validation.
  if (up.includes("±") || up.includes("+-")) {
    return { rowType: "numeric_tol", options: [] };
  }
  if (canonical) {
    return { rowType: canonical.rowType, options: canonical.options || [] };
  }
  if (up.includes("OK/NOT OK")) {
    return { rowType: "dropdown", options: ["OK", "NOT OK"] };
  }
  if (up.includes("AVAILABLE/NOT AVAILABLE")) {
    return { rowType: "dropdown", options: ["Available", "Not Available"] };
  }
  if (up.includes("OK/NOT OK/ADJUST")) {
    return { rowType: "dropdown", options: ["OK", "NOT OK", "ADJUST"] };
  }
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    return { rowType: "multi5", options: [] };
  }
  return { rowType: "text", options: [] };
}

function getSlotStatus(row, spec, raw) {
  return classifyCell(row.rowType, spec, raw);
}


function normalizeObservationForStorage(observation, rows) {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const out = { ...(observation || {}) };
  const numericRe = /^-?\d+(\.\d+)?$/;
  for (const [rowId, rowObs] of Object.entries(observation || {})) {
    const row = byId.get(rowId);
    if (!row || row.rowType !== "numeric_tol" || !rowObs || typeof rowObs !== "object") continue;
    const nextRowObs = { ...rowObs };
    for (const [slotKey, raw] of Object.entries(rowObs)) {
      const v = raw != null ? String(raw).trim() : "";
      if (!v || v.startsWith("+") || v.startsWith("-") || !numericRe.test(v)) continue;
      // Store explicit positive sign for unsigned numeric values (analytics-friendly).
      nextRowObs[slotKey] = `+${v}`;
    }
    out[rowId] = nextRowObs;
  }
  return out;
}

function normalizeMulti5ObservationForStorage(multi5Observation, multi5Rows) {
  const out = { ...(multi5Observation || {}) };
  const numericRe = /^-?\d+(\.\d+)?$/;
  for (const row of multi5Rows) {
    const rowObs = out[row.id];
    if (!rowObs || typeof rowObs !== "object") continue;
    const next = { ...rowObs };
    for (const ik of ["interval_0", "interval_1"]) {
      const arr = rowObs[ik];
      if (!Array.isArray(arr)) continue;
      next[ik] = arr.map((x) => {
        const v = x != null ? String(x).trim() : "";
        if (!v || v.startsWith("+") || v.startsWith("-") || !numericRe.test(v)) return v;
        return `+${v}`;
      });
    }
    out[row.id] = next;
  }
  return out;
}

function padMulti5IntervalArray(arr, pieces) {
  const n = Math.max(1, pieces);
  const base = Array.isArray(arr) ? arr.map((x) => (x == null ? "" : String(x))) : [];
  while (base.length < n) base.push("");
  return base.slice(0, n);
}

function InprocessInspectionList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const canEdit =
    user?.role === "SUPERVISOR" || user?.role === "OWNER" || user?.role === "MANAGER";

  useEffect(() => {
    api
      .get("/inspection-reports")
      .then((res) => setRows(res.data || []))
      .catch(() => toast.error("Failed to load reports"))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6 animate-fade-in p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">In-Process Inspection</h1>
          <p className="text-slate-500 text-sm mt-1">
            Phase 1 — observation grid, shift times, and production run comparison.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => navigate("/inprocess-inspection/new")}>
            <Plus className="w-4 h-4 mr-2" />
            New report
          </Button>
        )}
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Saved drafts</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500 text-sm">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-slate-500 text-sm">No reports yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-600">
                    <th className="py-2 pr-4">Batch</th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Status</th>
                    <th className="py-2 pr-4">Updated</th>
                    <th className="py-2"> </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100">
                      <td className="py-2 pr-4">{r.batch_no || "—"}</td>
                      <td className="py-2 pr-4">{r.form_data?.header?.date || "—"}</td>
                      <td className="py-2 pr-4 capitalize">{r.status}</td>
                      <td className="py-2 pr-4 text-slate-500">
                        {r.updated_at?.slice(0, 16)?.replace("T", " ") || "—"}
                      </td>
                      <td className="py-2">
                        <Link className="text-primary hover:underline" to={"/inprocess-inspection/" + r.id}>
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MismatchHints({ form, run }) {
  const tgt = form.target_quantity === "" ? null : Number(form.target_quantity);
  const tq = form.total_produced === "" ? null : Number(form.total_produced);
  const tr = form.total_rejected === "" ? null : Number(form.total_rejected);
  const ok = form.ok_quantity === "" ? null : Number(form.ok_quantity);
  const lines = [];
  if (tgt != null && !Number.isNaN(tgt) && !isNearlyEqual(tgt, run.target)) {
    lines.push(`Summary target (${tgt}) differs from run target (${run.target}).`);
  }
  if (tq != null && !Number.isNaN(tq) && !isNearlyEqual(tq, run.produced)) {
    lines.push(`Total produced (${tq}) differs from run produced (${run.produced}).`);
  }
  if (tr != null && !Number.isNaN(tr) && !isNearlyEqual(tr, run.rejected)) {
    lines.push(`Total rejected (${tr}) differs from run rejected (${run.rejected}).`);
  }
  if (ok != null && !Number.isNaN(ok) && !isNearlyEqual(ok, run.ok)) {
    lines.push(`OK quantity (${ok}) differs from run OK (${run.ok}).`);
  }
  if (!lines.length) return null;
  return (
    <ul className="mt-2 list-disc pl-5 text-amber-800 text-xs space-y-1">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}

function HourlyMismatchHints({ hourly, summary, run }) {
  const lines = [];
  if (run && !isNearlyEqual(hourly.target, run.target)) {
    lines.push(
      `Hourly target total (${formatTargetNumber(hourly.target)}) differs from run target (${formatTargetNumber(
        run.target
      )}).`
    );
  }
  if (
    summary.total_produced !== "" &&
    !isNearlyEqual(Number(summary.total_produced), hourly.produced)
  ) {
    lines.push(`Hourly produced total (${hourly.produced}) differs from summary total produced (${summary.total_produced}).`);
  }
  if (
    summary.target_quantity !== "" &&
    summary.target_quantity !== undefined &&
    !isNearlyEqual(Number(summary.target_quantity), hourly.target)
  ) {
    lines.push(
      `Hourly target total (${formatTargetNumber(hourly.target)}) differs from summary target (${summary.target_quantity}).`
    );
  }
  if (
    summary.total_rejected !== "" &&
    !isNearlyEqual(Number(summary.total_rejected), hourly.rejected)
  ) {
    lines.push(`Hourly rejected total (${hourly.rejected}) differs from summary rejected (${summary.total_rejected}).`);
  }
  const summaryOk = summary.ok_quantity === "" ? null : Number(summary.ok_quantity);
  if (summaryOk != null && !Number.isNaN(summaryOk) && !isNearlyEqual(summaryOk, hourly.ok)) {
    lines.push(`Hourly OK total (${hourly.ok}) differs from summary OK quantity (${summary.ok_quantity}).`);
  }
  if (run) {
    if (!isNearlyEqual(hourly.produced, run.produced)) {
      lines.push(`Hourly produced total (${hourly.produced}) differs from run produced (${run.produced}).`);
    }
    if (!isNearlyEqual(hourly.rejected, run.rejected)) {
      lines.push(`Hourly rejected total (${hourly.rejected}) differs from run rejected (${run.rejected}).`);
    }
  }
  if (!lines.length) return null;
  return (
    <ul className="mt-2 list-disc pl-5 text-amber-800 text-xs space-y-1">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}

function CellEditor({ row, value, onChange }) {
  if (row.rowType === "dropdown") {
    return (
      <Select value={value || "__empty__"} onValueChange={(v) => onChange(v === "__empty__" ? "" : v)}>
        <SelectTrigger className="h-8 text-xs border-0 shadow-none bg-transparent">
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__empty__">—</SelectItem>
          {(row.options || []).map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <Input
      className="h-8 text-xs border-0 shadow-none bg-transparent px-1"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="—"
    />
  );
}

function InprocessInspectionForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  /** `path="new"` matches /new without a :id param, so useParams().id is undefined here (not the string "new"). */
  const isNew = id === "new" || !id;
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(() => defaultFormData());
  const [reportId, setReportId] = useState(isNew ? null : id);
  const [productionRunId, setProductionRunId] = useState("");
  const [machines, setMachines] = useState([]);
  const [parts, setParts] = useState([]);
  const [operators, setOperators] = useState([]);
  const [productionRuns, setProductionRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);

  const canEdit =
    user?.role === "SUPERVISOR" || user?.role === "OWNER" || user?.role === "MANAGER";

  useEffect(() => {
    Promise.all([
      api.get("/machines"),
      api.get("/parts"),
      api.get("/operators"),
      api.get("/production-runs", { params: { page_size: 100 } }),
    ])
      .then(([m, p, o, r]) => {
        setMachines((m.data || []).filter((x) => x.active !== false));
        setParts(p.data || []);
        setOperators(o.data || []);
        setProductionRuns(r.data || []);
      })
      .catch(() => toast.error("Failed to load master data"));
  }, []);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    api
      .get("/inspection-reports/" + id)
      .then((res) => {
        if (cancelled) return;
        const d = res.data;
        setReportId(d.id);
        setProductionRunId(d.production_run_id || "");
        setFormData(mergeFormData(d.form_data));
      })
      .catch(() => {
        toast.error("Failed to load report");
        navigate("/inprocess-inspection");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, navigate]);

  useEffect(() => {
    if (!productionRunId) {
      setSelectedRun(null);
      return;
    }
    api
      .get("/production-runs/" + productionRunId)
      .then((res) => setSelectedRun(res.data))
      .catch(() => {
        setSelectedRun(null);
        toast.error("Could not load production run");
      });
  }, [productionRunId]);

  const selectedPart = useMemo(() => {
    const pid = formData.header.part_id;
    if (!pid) return null;
    return parts.find((p) => p.id === pid) || null;
  }, [parts, formData.header.part_id]);

  const parameterRows = useMemo(() => {
    const specs = selectedPart?.parameter_specs || [];
    return specs.map((s, idx) => {
      const pNorm = normalizeKey(s?.parameter || "");
      const canonical = CANONICAL_ROWS.find((r) => {
        const aliases = (r.aliases || []).map(normalizeKey);
        return aliases.some((a) => a && (pNorm === a || pNorm.includes(a) || a.includes(pNorm)));
      });
      const inferred = inferRowMeta(s?.specification, canonical);
      return {
        id: `spec_${s?.sequence ?? idx}_${normalizeKey(s?.parameter || "param")}`.replace(/\s+/g, "_"),
        label: (s?.parameter || "").trim() || `Parameter ${idx + 1}`,
        specification: (s?.specification || "").trim(),
        rowType: inferred.rowType,
        options: inferred.options,
        pieces_per_interval: Math.max(1, Number(s?.pieces_per_interval) || 1),
        sequence: s?.sequence == null || s?.sequence === "" ? idx : Number(s.sequence),
      };
    });
  }, [selectedPart]);

  const displayedRows = useMemo(
    () =>
      parameterRows.filter((row) => row.rowType !== "multi5" && row.pieces_per_interval <= 1),
    [parameterRows]
  );

  const multi5Rows = useMemo(
    () => parameterRows.filter((row) => row.rowType === "multi5" || row.pieces_per_interval > 1),
    [parameterRows]
  );

  const halfWorkingIntervals = useMemo(
    () => buildTwoHalfWorkingIntervals(formData.shift.shift_start, formData.shift.shift_end),
    [formData.shift.shift_start, formData.shift.shift_end]
  );

  const calendarShiftHoursLabel = useMemo(() => {
    const a = formData.shift.shift_start;
    const b = formData.shift.shift_end;
    if (!a || !b) return "";
    const sm = (x) => {
      const [h, m] = String(x).split(":").map((n) => parseInt(n, 10));
      if (Number.isNaN(h)) return x;
      return `${String(h).padStart(2, "0")}:${String(Number.isNaN(m) ? 0 : m).padStart(2, "0")}`;
    };
    return `${sm(a)}–${sm(b)}`;
  }, [formData.shift.shift_start, formData.shift.shift_end]);

  const runTotals = useMemo(() => aggregateRunTotals(selectedRun), [selectedRun]);
  const okFromRun = Math.max(0, runTotals.produced - runTotals.rejected);

  const hourlySlots = useMemo(
    () => buildHourlySlots(formData.shift.shift_start, formData.shift.shift_end),
    [formData.shift.shift_start, formData.shift.shift_end]
  );
  const observationSlots = useMemo(
    () => buildObservationSlots(formData.shift.shift_start, formData.shift.shift_end),
    [formData.shift.shift_start, formData.shift.shift_end]
  );

  const updateHeader = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, header: { ...prev.header, [field]: value } }));
  }, []);

  const onPartChange = useCallback(
    (v) => {
      setFormData((prev) => {
        const part = parts.find((p) => String(p.id) === String(v));
        const drawingFromPart =
          part && part.drawing_no != null ? String(part.drawing_no).trim() : "";
        return {
          ...prev,
          header: {
            ...prev.header,
            part_id: v,
            drawing_no: part ? drawingFromPart : prev.header.drawing_no,
          },
        };
      });
    },
    [parts]
  );

  const updateShift = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, shift: { ...prev.shift, [field]: value } }));
  }, []);

  const updateSummary = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      summary_quantities: { ...prev.summary_quantities, [field]: value },
    }));
  }, []);

  const updateHourly = useCallback((slotKey, field, value) => {
    setFormData((prev) => ({
      ...prev,
      hourly_production: {
        ...(prev.hourly_production || {}),
        [slotKey]: {
          ...((prev.hourly_production && prev.hourly_production[slotKey]) || {}),
          [field]: value,
        },
      },
    }));
  }, []);
  useEffect(() => {
    const computed = computePerHourTargetFromCycle(
      formData.header.cycle_time,
      formData.shift.shift_start,
      formData.shift.shift_end,
      hourlySlots.length
    );
    if (computed == null || hourlySlots.length === 0) return;
    setFormData((prev) => {
      const nextHourly = { ...(prev.hourly_production || {}) };
      let changed = false;
      const formatted = formatTargetNumber(computed);
      for (const slot of hourlySlots) {
        const row = { ...(nextHourly[slot.key] || {}) };
        if (String(row.target_qty ?? "") !== formatted) changed = true;
        row.target_qty = formatted;
        nextHourly[slot.key] = row;
      }
      if (!changed) return prev;
      return { ...prev, hourly_production: nextHourly };
    });
  }, [formData.header.cycle_time, formData.shift.shift_start, formData.shift.shift_end, hourlySlots]);

  const setObservationCell = useCallback((rowId, slotKey, value) => {
    setFormData((prev) => ({
      ...prev,
      observation: {
        ...prev.observation,
        [rowId]: { ...prev.observation[rowId], [slotKey]: value },
      },
    }));
  }, []);

  const multi5MaxPieces = useMemo(
    () => (multi5Rows.length ? Math.max(...multi5Rows.map((r) => r.pieces_per_interval), 1) : 1),
    [multi5Rows]
  );

  const setMulti5Piece = useCallback((rowId, intervalKey, pieceIndex, value, pieces) => {
    setFormData((prev) => {
      const prevM = { ...(prev.multi5_observation || {}) };
      const row = { ...(prevM[rowId] || {}) };
      const arr = padMulti5IntervalArray(row[intervalKey], pieces);
      const next = [...arr];
      next[pieceIndex] = value;
      row[intervalKey] = next;
      prevM[rowId] = row;
      return { ...prev, multi5_observation: prevM };
    });
  }, []);

  const saveDraft = async () => {
    if (!canEdit) return;
    const h = formData.header;
    const sh = formData.shift;
    const checks = [
      [!h.date?.trim(), "Date is required"],
      [!h.shift, "Shift is required"],
      [!sh.shift_start?.trim(), "Shift start is required"],
      [!sh.shift_end?.trim(), "Shift end is required"],
      [!h.machine_id, "Machine is required"],
      [!h.part_id, "Part is required"],
      [!(h.batch_no || "").trim(), "Batch no. is required"],
      [!(h.heat_no || "").trim(), "Heat no. is required"],
      [!(h.cycle_time || "").toString().trim(), "Cycle time (sec) is required"],
      [
        Number.isNaN(parseInt(h.cycle_time, 10)) || parseInt(h.cycle_time, 10) < 0,
        "Enter a valid cycle time (seconds)",
      ],
      [!h.operator_id, "Operator is required"],
      [!(h.drawing_no || "").trim(), "Drawing no. is required"],
      [!productionRunId, "Production run is required"],
    ];
    const failed = checks.find((c) => c[0]);
    if (failed) {
      toast.error(failed[1]);
      return;
    }
    setSaving(true);
    try {
      const normalizedObservation = normalizeObservationForStorage(formData.observation, displayedRows);
      const normalizedMulti5 = normalizeMulti5ObservationForStorage(
        formData.multi5_observation,
        multi5Rows
      );
      const payload = {
        production_run_id: productionRunId || null,
        batch_no: formData.header.batch_no || "",
        form_data: { ...formData, observation: normalizedObservation, multi5_observation: normalizedMulti5 },
      };
      if (isNew || !reportId) {
        const res = await api.post("/inspection-reports", payload);
        setReportId(res.data.id);
        toast.success("Report created");
        navigate("/inprocess-inspection/" + res.data.id, { replace: true });
      } else {
        await api.put("/inspection-reports/" + reportId, payload);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const machineOptions = useMemo(
    () => machines.map((m) => ({ value: m.id, label: m.code })),
    [machines]
  );
  const partOptions = useMemo(() => parts.map((p) => ({ value: p.id, label: p.part_no })), [parts]);
  const operatorOptions = useMemo(
    () => operators.map((o) => ({ value: o.id, label: o.name })),
    [operators]
  );
  const runOptions = useMemo(
    () =>
      productionRuns.map((r) => ({
        value: r.id,
        label: r.date + " · " + (r.machine_code || "") + " · " + (r.part_no || "") + " · " + (r.status || ""),
      })),
    [productionRuns]
  );
  const hourlyTotals = useMemo(() => {
    const hp = formData.hourly_production || {};
    let target = 0;
    let produced = 0;
    let rejected = 0;
    for (const slot of hourlySlots) {
      const row = hp[slot.key] || {};
      target += Number(row.target_qty) || 0;
      produced += Number(row.produced_qty) || 0;
      rejected += Number(row.rejected_qty) || 0;
    }
    return { target, produced, rejected, ok: Math.max(0, produced - rejected) };
  }, [formData.hourly_production, hourlySlots]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[240px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="p-6">
        <p className="text-slate-500">You do not have permission to edit in-process inspection reports.</p>
      </div>
    );
  }

  const inputClass = "h-10";

  return (
    <div className="space-y-6 animate-fade-in p-4 md:p-6 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/inprocess-inspection">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Link>
        </Button>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {isNew ? "New in-process inspection" : "Edit report"}
        </h1>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Header & context</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Date</Label>
            <Input
              type="date"
              value={formData.header.date}
              onChange={(e) => updateHeader("date", e.target.value)}
              className={`${inputClass} w-40`}
              data-testid="input-date"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Shift</Label>
            <div className="w-40">
              <Select value={formData.header.shift || "DAY"} onValueChange={(v) => updateHeader("shift", v)}>
                <SelectTrigger className={`${inputClass} w-full`} data-testid="input-header-shift">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="DAY">Day</SelectItem>
                  <SelectItem value="NIGHT">Night</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Shift start</Label>
            <Input
              type="time"
              className={`${inputClass} w-40 font-mono`}
              value={formData.shift.shift_start}
              onChange={(e) => updateShift("shift_start", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Shift end</Label>
            <Input
              type="time"
              className={`${inputClass} w-40 font-mono`}
              value={formData.shift.shift_end}
              onChange={(e) => updateShift("shift_end", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Machine</Label>
            <div className="w-40">
              <SearchableDropdownBox
                options={machineOptions}
                value={formData.header.machine_id}
                onChange={(v) => updateHeader("machine_id", v)}
                placeholder="Select"
                triggerClassName="!h-10 w-full"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Part</Label>
            <SearchableDropdownBox
              options={partOptions}
              value={formData.header.part_id}
              onChange={onPartChange}
              placeholder="Select"
              triggerClassName="!h-10 w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Batch no.</Label>
            <div className="w-40">
              <Input
                className={`${inputClass} w-full`}
                value={formData.header.batch_no}
                onChange={(e) => updateHeader("batch_no", e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Heat no.</Label>
            <div className="w-40">
              <Input
                className={`${inputClass} w-full`}
                value={formData.header.heat_no}
                onChange={(e) => updateHeader("heat_no", e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Cycle time (sec)</Label>
            <Input
              type="number"
              min={0}
              className={`${inputClass} w-40 font-mono`}
              value={formData.header.cycle_time}
              onChange={(e) => updateHeader("cycle_time", e.target.value)}
              placeholder="0"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Operator</Label>
            <SearchableDropdownBox
              options={operatorOptions}
              value={formData.header.operator_id}
              onChange={(v) => updateHeader("operator_id", v)}
              placeholder="Select"
              triggerClassName="!h-10 w-full"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Drawing no.</Label>
            <Input
              className={`${inputClass} w-full`}
              value={formData.header.drawing_no}
              onChange={(e) => updateHeader("drawing_no", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Production run</Label>
            <SearchableDropdownBox
              options={runOptions}
              value={productionRunId}
              onChange={(v) => setProductionRunId(v ? String(v) : "")}
              placeholder="Select"
              triggerClassName="!h-10 w-full"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Observation</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {selectedPart && displayedRows.length === 0 ? (
            <p className="text-sm text-slate-500">
              {(selectedPart.parameter_specs || []).length === 0
                ? "No parameter specifications found for selected part."
                : "No observation rows to display. Parameters with pieces > 1 or multi5-type specs use the Multi-value table below."}
            </p>
          ) : null}
          <table className="w-full text-xs border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-2 sticky left-0 bg-white z-10">Parameter</th>
                <th className="text-left py-2 pr-2 min-w-[100px]">Specification</th>
                {observationSlots.map((slot) => (
                  <th key={slot.key} className="text-center py-2 px-1 min-w-[72px]">
                    {slot.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {displayedRows.map((row) => {
                const spec = row.specification;
                return (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="py-1 pr-2 align-top sticky left-0 bg-white font-medium">{row.label}</td>
                    <td className="py-1 pr-2 align-top text-slate-600 break-words max-w-[120px]">
                      {spec ? formatSpecificationDisplay(spec) : "—"}
                    </td>
                    {observationSlots.map((slot) => {
                      const raw = formData.observation[row.id]?.[slot.key] ?? "";
                      const st = getSlotStatus(row, spec, raw);
                      const warnEmpty = !String(raw || "").trim();
                      let cellClass = "border border-slate-200 ";
                      if (st === "pass") cellClass += "bg-emerald-50 ";
                      else if (st === "fail") cellClass += "bg-red-50 ";
                      else if (warnEmpty) cellClass += "bg-amber-50 ";
                      return (
                        <td key={slot.key} className={"p-0.5 " + cellClass}>
                          <CellEditor
                            row={row}
                            value={raw}
                            onChange={(v) => setObservationCell(row.id, slot.key, v)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {selectedPart ? (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Multi-value (5×) observation</CardTitle>
            <CardDescription>
              {halfWorkingIntervals.length > 0 ? (
                <>
                  Calendar shift {calendarShiftHoursLabel} · Effective working time{" "}
                  {computeWorkingHoursFromShift(formData.shift.shift_start, formData.shift.shift_end)}h · Intervals:{" "}
                  {halfWorkingIntervals.map((h) => h.label).join(" · ")}
                </>
              ) : (
                "Set shift start and end to define two working-time intervals."
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {multi5Rows.length === 0 ? (
              <p className="text-sm text-slate-500">
                No multi-value rows for this part (multi5 specification or pieces greater than 1 in master data).
              </p>
            ) : halfWorkingIntervals.length === 0 ? (
              <p className="text-sm text-amber-800">Effective working time is zero; check shift start and end.</p>
            ) : (
              <table className="w-full text-xs border-collapse min-w-[640px]">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 pr-2 sticky left-0 bg-white z-10">Parameter</th>
                    <th className="text-left py-2 pr-2 min-w-[100px]">Specification</th>
                    {halfWorkingIntervals.map((h) => (
                      <th
                        key={h.key}
                        colSpan={multi5MaxPieces}
                        className="text-center py-2 px-1 border-l border-slate-200"
                      >
                        {h.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {multi5Rows.map((row) => {
                    const spec = row.specification;
                    const pieces = row.pieces_per_interval;
                    return (
                      <tr key={row.id} className="border-b border-slate-100">
                        <td className="py-1 pr-2 align-top sticky left-0 bg-white font-medium">{row.label}</td>
                        <td className="py-1 pr-2 align-top text-slate-600 break-words max-w-[120px]">
                          {spec ? formatSpecificationDisplay(spec) : "—"}
                        </td>
                        {halfWorkingIntervals.flatMap((hi) =>
                          Array.from({ length: multi5MaxPieces }, (_, i) => {
                            const active = i < pieces;
                            const m5row = formData.multi5_observation?.[row.id] || {};
                            const vals = padMulti5IntervalArray(m5row[hi.key], pieces);
                            const raw = active ? vals[i] ?? "" : "";
                            const st = active ? classifyCell(row.rowType, spec, raw) : "empty";
                            const warnEmpty = active && !String(raw || "").trim();
                            let cellClass = "border border-slate-200 ";
                            if (!active) cellClass += "bg-slate-50 ";
                            else if (st === "pass") cellClass += "bg-emerald-50 ";
                            else if (st === "fail") cellClass += "bg-red-50 ";
                            else if (warnEmpty) cellClass += "bg-amber-50 ";
                            return (
                              <td key={`${row.id}-${hi.key}-${i}`} className={"p-0.5 " + cellClass}>
                                {active ? (
                                  <Input
                                    className="h-8 text-xs border-0 shadow-none bg-transparent px-1"
                                    value={raw}
                                    onChange={(e) =>
                                      setMulti5Piece(row.id, hi.key, i, e.target.value, pieces)
                                    }
                                    placeholder={`${i + 1}`}
                                  />
                                ) : (
                                  <span className="block h-8" />
                                )}
                              </td>
                            );
                          })
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Hourly production</CardTitle>
          <CardDescription>Capture produced/rejected quantities by time slot.</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-xs border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-2">Metric</th>
                {hourlySlots.map((slot) => (
                  <th key={slot.key} className="text-center py-2 px-1 min-w-[96px]">
                    {slot.label}
                  </th>
                ))}
                <th className="text-center py-2 px-2 min-w-[90px]">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-slate-100 bg-slate-50/60">
                <td className="py-1 pr-2 font-medium">Target</td>
                {hourlySlots.map((slot) => (
                  <td key={slot.key} className="p-0.5">
                    <Input
                      inputMode="numeric"
                      className="h-8 text-xs"
                      value={formData.hourly_production?.[slot.key]?.target_qty ?? ""}
                      onChange={(e) => updateHourly(slot.key, "target_qty", e.target.value)}
                      placeholder={formatTargetNumber(
                        computePerHourTargetFromCycle(
                          formData.header.cycle_time,
                          formData.shift.shift_start,
                          formData.shift.shift_end,
                          hourlySlots.length
                        ) ?? 0
                      )}
                    />
                  </td>
                ))}
                <td className="text-center font-mono text-slate-700">
                  {formatTargetNumber(hourlyTotals.target)}
                </td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-1 pr-2 font-medium">Produced</td>
                {hourlySlots.map((slot) => (
                  <td key={slot.key} className="p-0.5">
                    <Input
                      inputMode="numeric"
                      className="h-8 text-xs"
                      value={formData.hourly_production?.[slot.key]?.produced_qty ?? ""}
                      onChange={(e) => updateHourly(slot.key, "produced_qty", e.target.value)}
                      placeholder="0"
                    />
                  </td>
                ))}
                <td className="text-center font-mono text-slate-700">{hourlyTotals.produced}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="py-1 pr-2 font-medium">Rejected</td>
                {hourlySlots.map((slot) => (
                  <td key={slot.key} className="p-0.5">
                    <Input
                      inputMode="numeric"
                      className="h-8 text-xs"
                      value={formData.hourly_production?.[slot.key]?.rejected_qty ?? ""}
                      onChange={(e) => updateHourly(slot.key, "rejected_qty", e.target.value)}
                      placeholder="0"
                    />
                  </td>
                ))}
                <td className="text-center font-mono text-slate-700">{hourlyTotals.rejected}</td>
              </tr>
              <tr>
                <td className="py-1 pr-2 font-medium">Remarks</td>
                {hourlySlots.map((slot) => (
                  <td key={slot.key} className="p-0.5">
                    <Input
                      className="h-8 text-xs"
                      value={formData.hourly_production?.[slot.key]?.remarks ?? ""}
                      onChange={(e) => updateHourly(slot.key, "remarks", e.target.value)}
                      placeholder="—"
                    />
                  </td>
                ))}
                <td className="text-center font-mono text-slate-700">{hourlyTotals.ok}</td>
              </tr>
            </tbody>
          </table>
          <HourlyMismatchHints
            hourly={hourlyTotals}
            summary={formData.summary_quantities}
            run={selectedRun ? { target: runTotals.target, produced: runTotals.produced, rejected: runTotals.rejected } : null}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Summary quantities (compare with production run)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label>Target</Label>
              <Input
                inputMode="decimal"
                value={formData.summary_quantities.target_quantity}
                onChange={(e) => updateSummary("target_quantity", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Total produced</Label>
              <Input
                inputMode="numeric"
                value={formData.summary_quantities.total_produced}
                onChange={(e) => updateSummary("total_produced", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Total rejected</Label>
              <Input
                inputMode="numeric"
                value={formData.summary_quantities.total_rejected}
                onChange={(e) => updateSummary("total_rejected", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>OK quantity</Label>
              <Input
                inputMode="numeric"
                value={formData.summary_quantities.ok_quantity}
                onChange={(e) => updateSummary("ok_quantity", e.target.value)}
              />
            </div>
          </div>
          {selectedRun && (
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
              <p className="font-medium text-slate-700 mb-2">Production run totals</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <div>
                  <span className="text-slate-500">Target </span>
                  <span className="font-mono">{runTotals.target}</span>
                </div>
                <div>
                  <span className="text-slate-500">Produced </span>
                  <span className="font-mono">{runTotals.produced}</span>
                </div>
                <div>
                  <span className="text-slate-500">Rejected </span>
                  <span className="font-mono">{runTotals.rejected}</span>
                </div>
                <div>
                  <span className="text-slate-500">OK (produced − rej.) </span>
                  <span className="font-mono">{okFromRun}</span>
                </div>
              </div>
              <MismatchHints
                form={formData.summary_quantities}
                run={{ target: runTotals.target, produced: runTotals.produced, rejected: runTotals.rejected, ok: okFromRun }}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2 pb-8">
        <Button variant="outline" onClick={saveDraft} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save draft
        </Button>
      </div>
    </div>
  );
}

export default function InprocessInspectionPage() {
  return (
    <Routes>
      <Route index element={<InprocessInspectionList />} />
      <Route path="new" element={<InprocessInspectionForm />} />
      <Route path=":id" element={<InprocessInspectionForm />} />
    </Routes>
  );
}
